import isEmpty from 'lodash/isEmpty';

import { FILTER_KEY, type FiltersKey } from '@/ducks/filters/types';
import uniq from '@/helpers/uniq';
import { ensureArray } from '@/helpers/util/misc';

const toArray = (value: unknown) => {
  let arr: unknown[] = [];
  if (Array.isArray(value)) {
    arr = value;
  } else {
    if (typeof value === 'string' && value.indexOf(',') !== -1) {
      arr = value.split(/\s*,\s*/);
    } else {
      arr = [value];
    }
  }

  return arr;
};

export const toInt = (value: unknown) => {
  const int = parseInt(value as string, 10);
  return isNaN(int) ? null : int;
};

export const toIntArray = (value: unknown) => {
  return toArray(value).reduce<number[]>((arr, val) => {
    const int = parseInt(val as string, 10);
    return isNaN(int) ? arr : [...arr, int];
  }, []);
};

export const toStringArray = (value: unknown) => {
  return toArray(value).reduce<string[]>((arr, val) => {
    const str = val?.toString();
    return isEmpty(str) ? arr : [...arr, str!];
  }, []);
};

export const toBoolean = (value: null | unknown) => {
  return value !== null && value !== 'false' && value !== '0';
};

export function isDateValidNative(inputDate: string): boolean {
  const [year, month, day] = inputDate.split('-');
  const yearInt = parseInt(year!, 10);
  const monthInt = parseInt(month!, 10) - 1;
  const dayInt = parseInt(day!, 10);
  const tryDate = new Date(yearInt, monthInt, dayInt);

  return tryDate.getFullYear() === yearInt && tryDate.getMonth() === monthInt && tryDate.getDate() === dayInt;
}

const mapFilterKeyToSearchParam: { [key in FiltersKey]?: string | string[] } = {
  [FILTER_KEY.accessible]: ['isCabinAccessible'],
  [FILTER_KEY.dateFrom]: ['dateFrom', 'fromDate'],
  [FILTER_KEY.dateTo]: ['dateTo', 'toDate'],
  [FILTER_KEY.destPackages]: ['selectedPackages', 'selectedPackage'],
  [FILTER_KEY.destPackagesRegions]: [
    'selectedRegions',
    'selectedRegion',
    'selectedRegionsIds',
    'selectedPackagesRegions',
  ],
  [FILTER_KEY.destPorts]: ['selectedPorts', 'selectedPort'],
  [FILTER_KEY.destPortsRegions]: ['selectedPortsRegion', 'selectedPortsRegions'],
  [FILTER_KEY.priceMax]: ['priceMax', 'maxPrice'],
  [FILTER_KEY.priceMin]: ['priceMin', 'minPrice'],
  [FILTER_KEY.ships]: ['ships', 'shipCode', 'selectedShipList'],
  [FILTER_KEY.weekend]: ['weekend', 'isWeekend'],
};

export const getSearchParamsKeys = (key: FiltersKey) => {
  return ensureArray(mapFilterKeyToSearchParam[key as FiltersKey] || [key]) as FiltersKey[];
};

export const getSearchParamsValue = (key: FiltersKey, searchParams: URLSearchParams): null | string | string[] => {
  const foundKey = getSearchParamsKeys(key).find((key) => searchParams.has(key));
  if (foundKey) {
    const values = uniq(searchParams.getAll(foundKey));
    return values.length > 1 ? values : values[0]!;
  }

  return null;
};
